import { z } from 'zod';

/**
 * `rooms.v1`: rooms on a LiveKit server, where the core's voice calls run when it speaks itself
 * (its pipeline, a group's voices). The provider opens a room, lets participants in, and closes
 * it; the core only knows LiveKit: each participant gets the server's URL and a LiveKit token.
 * Two roles: the person (`user`), and the core's voices (`bot`): microphone only, data allowed.
 * The core calls it; pages never do.
 */
export const roomIdSchema = z
  .string()
  .min(1)
  .max(256)
  .regex(/^[A-Za-z0-9._:-]+$/);

export const roomRoleSchema = z.enum(['user', 'bot']);
export type RoomRole = z.infer<typeof roomRoleSchema>;

export const roomsV1 = {
  'room.open': {
    input: z.object({ name: z.string().min(1).max(200) }).strict(),
    output: z.object({ room: roomIdSchema }).strict(),
  },
  /** Lets one participant in: `peer` is unique in the room (one per voice). */
  'room.join': {
    input: z
      .object({
        room: roomIdSchema,
        role: roomRoleSchema,
        peer: z
          .string()
          .min(1)
          .max(128)
          .regex(/^[A-Za-z0-9._:-]+$/),
        name: z.string().min(1).max(120),
      })
      .strict(),
    output: z
      .object({
        /** The LiveKit server (`wss://…`). */
        url: z.string().regex(/^wss?:\/\//),
        token: z.string().min(1).max(8192),
      })
      .strict(),
  },
  /** Ends the room for everyone in it and removes it. */
  'room.close': {
    input: z.object({ room: roomIdSchema }).strict(),
    output: z.object({}).strict(),
  },
} as const;
